import React from "react";
import HttpServices from "../../utils/Http.services";
import { translate, checkTranslation } from "../../utils/useful";
import { Doughnut } from "react-chartjs-2";
import { siteTheme } from "../../variables/config";
import { processStages } from "../../variables/userWidgets";
import { getRecordProcessStageKey } from "../../utils/processStageAccess";
import { buildProcessWidgetRequest } from "../../utils/widgetRequests";
import { fakeFormsData } from "../../variables/fakeData";

const colors = siteTheme.primaryColors

class ContractsOverviewWidget extends React.Component {
    state = {
        isLoading: true,
        data: [],
        lastFollowUpResultCounts: {},
        lastFollowUpResultColors: {},
        lastFollowUpResultBackgrounds: {},
        lastFollowUpResultTextColors: {},
        currentStatusCounts: {},
        currentStatusColors: {},
        totalCount: 0,
        contractsCount: 0,
        error: null,
        leftChartData: {
            labels: [],
            datasets: [{
                data: [],
                backgroundColor: [],
            }],
        },
        rightChartData: {
            labels: [],
            datasets: [{
                data: [],
                backgroundColor: [],
            }],
        },
        showLegend: false,
    }

    componentDidMount() {
        this.fetchData()
        window.addEventListener('reloadFetchData', this.handleReloadFetchData)
    }

    componentWillUnmount() {
        window.removeEventListener('reloadFetchData', this.handleReloadFetchData)
    }

    handleReloadFetchData = () => {
        this.fetchData()
    }

    componentDidUpdate(prevProps) {
        const widget = this.props.widget || this.props.data
        const prevWidget = prevProps.widget || prevProps.data
        const variables = this.props.variables || {}
        const prevVariables = prevProps.variables || {}
        
        if (widget !== prevWidget ||
            widget?.postBody?.applet !== prevWidget?.postBody?.applet ||
            variables?.applet !== prevVariables?.applet ||
            this.props.useFakeData !== prevProps.useFakeData) {
            this.fetchData()
        }
    }

    fetchData = () => {
        const widget = this.props.widget || this.props.data
        const variables = this.props.variables || {}
        
        this.setState({ isLoading: true, error: null })

        const body = buildProcessWidgetRequest({
            widget,
            variables,
        })

        if (this.props.useFakeData) {
            const previewFrame = body.frame || body.filter?.frame
            const records = fakeFormsData.filter(record => {
                return !previewFrame
                    || record.frame === previewFrame
                    || record.page === previewFrame
            })
            this.processData(records)
            return
        }

        HttpServices.request(widget.fetchUrl || 'getRecords', body, (fetchResult, fetchError) => {
            if (fetchError) {
                console.error('Error fetching data:', fetchError)
                this.setState({
                    isLoading: false,
                    error: fetchError,
                })
                return
            }

            const records = fetchResult?.info || []
            this.processData(records)
        })
    }

    processData = (records) => {
        // Define all available lastFollowUpResult statuses with their colors and backgrounds
        const allStatuses = [
            { value: 'در حال مذاکره', title: 'در حال مذاکره', color: '#fff', background: 'rgb(0, 122, 255)' },
            { value: 'منصرف شده', title: 'منصرف شده', background: '#ee5050', color: '#fff' },
            { value: 'پیش قرارداد', title: 'پیش قرارداد', color: '#000', background: 'rgb(255, 197, 0)' },
            { value: 'رزرو', title: 'رزرو', color: '#000', background: 'rgb(127, 223, 240)' },
            { value: 'قرارداد', title: 'قرارداد', color: '#fff', background: 'rgb(0, 205, 116)' }
        ]

        // Initialize counts for all statuses
        const lastFollowUpResultCounts = {}
        const lastFollowUpResultColors = {}
        const lastFollowUpResultBackgrounds = {}
        const lastFollowUpResultTextColors = {}

        allStatuses.forEach(status => {
            lastFollowUpResultCounts[status.value] = 0
            lastFollowUpResultColors[status.value] = status.background
            lastFollowUpResultBackgrounds[status.value] = status.background
            lastFollowUpResultTextColors[status.value] = status.color
        })

        // Count records by lastFollowUpResult
        records.forEach(record => {
            const status = record.values?.lastFollowUpResult
            if (status && lastFollowUpResultCounts.hasOwnProperty(status)) {
                lastFollowUpResultCounts[status]++
            }
        })

        // Process currentStatus distribution (only contracts)
        const contractRecords = records.filter(record => 
            record.values?.lastFollowUpResult === 'قرارداد' && 
            getRecordProcessStageKey(record)
        )

        const currentStatusCounts = {}
        const currentStatusColors = {}
        const stageKeys = Object.keys(processStages)

        // Initialize counts
        stageKeys.forEach(stageKey => {
            currentStatusCounts[stageKey] = 0
        })

        // Count by currentStatus (which is an object with a key property)
        contractRecords.forEach(record => {
            const statusKey = getRecordProcessStageKey(record)
            if (statusKey && currentStatusCounts.hasOwnProperty(statusKey)) {
                currentStatusCounts[statusKey]++
            }
        })

        // Assign colors from processStages for all stages
        stageKeys.forEach(key => {
            currentStatusColors[key] = processStages[key]?.color || '#999'
        })

        // Prepare left chart data (lastFollowUpResult) - show all statuses, even with 0 count
        const leftLabels = allStatuses.map(s => s.value)
        const leftData = leftLabels.map(label => lastFollowUpResultCounts[label] || 0)
        const leftBackgroundColors = leftLabels.map(label => lastFollowUpResultColors[label])

        // Prepare right chart data (currentStatus for contracts) - show all stages, even with 0 count
        const rightLabels = stageKeys.filter(key => currentStatusCounts[key] > 0)
        const rightData = rightLabels.map(key => currentStatusCounts[key] || 0)
        const rightBackgroundColors = rightLabels.map(key => currentStatusColors[key] || processStages[key]?.color || '#999')

        this.setState({
            data: records,
            lastFollowUpResultCounts,
            lastFollowUpResultColors,
            lastFollowUpResultBackgrounds,
            lastFollowUpResultTextColors,
            currentStatusCounts,
            currentStatusColors,
            totalCount: records.length,
            contractsCount: contractRecords.length,
            leftChartData: {
                labels: leftLabels,
                datasets: [{
                    data: leftData,
                    backgroundColor: leftBackgroundColors,
                }],
            },
            rightChartData: {
                labels: rightLabels.map(key => checkTranslation(processStages[key]?.name || key)),
                datasets: [{
                    data: rightData,
                    backgroundColor: rightBackgroundColors,
                }],
            },
            isLoading: false,
            error: null,
        })
    }

    render() {
        const { 
            isLoading, 
            lastFollowUpResultCounts, 
            lastFollowUpResultBackgrounds,
            currentStatusCounts,
            totalCount,
            contractsCount,
            leftChartData,
            rightChartData,
            showLegend,
            error,
        } = this.state

        if (isLoading) {
            return (
                <div className="w-100 py-3 text-center">
                    <p>{translate('Loading...')}</p>
                </div>
            )
        }

        if (error) {
            return (
                <div className="w-100 py-4 text-center" role="alert">
                    <p className="mb-0" style={{ color: '#b42318' }}>
                        {translate('Unable to load dashboard data')}
                    </p>
                </div>
            )
        }

        // Get all lastFollowUpResult statuses for top boxes (all available statuses)
        const statusKeys = Object.keys(lastFollowUpResultCounts)

        return (
            <div className="w-100 py-2">
                <div className="row m-0">
                    {/* Status boxes - lastFollowUpResult */}
                    <div className="col-12 mb-3">
                        <div className="row m-0">
                            {statusKeys.map((status) => {
                                const count = lastFollowUpResultCounts[status] || 0
                                const percentage = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0
                                const backgroundColor = lastFollowUpResultBackgrounds[status] || '#ccc'
                                return (
                                    <div key={status} className="col-6 col-md-4 col-lg-2 mb-2">
                                        <div className="p-3 h-100" style={{
                                            background: `linear-gradient(135deg, ${backgroundColor}15, #ffffff)`,
                                            border: `1px solid ${backgroundColor}40`,
                                            borderRadius: 12,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}>
                                            <p className="mb-1" style={{ fontSize: 24, fontWeight: 'bold', color: backgroundColor }}>
                                                {count}
                                            </p>
                                            <p className="mb-0" style={{ fontSize: 11, color: '#666' }}>
                                                {checkTranslation(status)}
                                            </p>
                                            <p className="mb-0" style={{ fontSize: 10, color: '#999' }}>
                                                {percentage}%
                                            </p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Left Chart - lastFollowUpResult distribution */}
                    {leftChartData.labels.length > 0 && (
                        <div className="col-12 col-md-6">
                            <div className="p-3" style={{
                                background: '#fff',
                                borderRadius: 12,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                overflow: 'visible'
                            }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0" style={{ fontSize: 14, fontWeight: 600 }}>
                                        {checkTranslation('{{lang}}All Status Distribution')}
                                    </h6>
                                    <button
                                        onClick={() => this.setState({ showLegend: !this.state.showLegend })}
                                        style={{
                                            background: showLegend
                                                ? `linear-gradient(135deg, ${colors.main1} 0%, ${colors.main1} 75%, ${colors.main2} 100%)`
                                                : `linear-gradient(135deg, ${colors.main3}50, ${colors.main3}60)`,
                                            border: `2px solid ${showLegend ? colors.main1 : colors.main3 + '80'}`,
                                            borderRadius: 20,
                                            padding: '6px 16px',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: showLegend ? '#fff' : colors.main1,
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            boxShadow: showLegend ? `0 2px 8px ${colors.main1}30` : 'none'
                                        }}
                                    >
                                        {showLegend ? translate('Hide Legend') : translate('Show Legend')}
                                    </button>
                                </div>
                                <div style={{ height: 350, padding: '20px', overflow: 'hidden', position: 'relative', minHeight: 350 }}>
                                    <Doughnut
                                        data={leftChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            cutout: '25%',
                                            layout: {
                                                padding: {
                                                    left: 0,
                                                    right: 0,
                                                    top: 0,
                                                    bottom: showLegend ? 50 : 0
                                                }
                                            },
                                            plugins: {
                                                legend: {
                                                    display: showLegend,
                                                    position: 'bottom',
                                                    align: 'center',
                                                    labels: {
                                                        boxWidth: 14,
                                                        padding: 10,
                                                        font: { size: 11, weight: '500' },
                                                        usePointStyle: true,
                                                        pointStyle: 'circle',
                                                        generateLabels: function (chart) {
                                                            const data = chart.data
                                                            if (data.labels.length && data.datasets.length) {
                                                                return data.labels.map((label, i) => {
                                                                    const value = data.datasets[0].data[i]
                                                                    const color = data.datasets[0].backgroundColor[i]
                                                                    const total = data.datasets[0].data.reduce((a, b) => a + b, 0)
                                                                    const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                                    return {
                                                                        text: `${label}: ${value} (${percentage}%)`,
                                                                        fillStyle: color,
                                                                        strokeStyle: color,
                                                                        lineWidth: 0,
                                                                        hidden: false,
                                                                        index: i
                                                                    }
                                                                })
                                                            }
                                                            return []
                                                        }
                                                    }
                                                },
                                                datalabels: {
                                                    display: false
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: function (context) {
                                                            const label = context.label || ''
                                                            const value = context.parsed || context.raw
                                                            const total = context.dataset.data.reduce((a, b) => a + b, 0)
                                                            const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                            return `${label}: ${value} (${percentage}%)`
                                                        }
                                                    }
                                                }
                                            },
                                            elements: {
                                                arc: {
                                                    borderWidth: 2,
                                                    borderColor: '#fff'
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Right Chart - process stage distribution for contracts */}
                    {rightChartData.labels.length > 0 && (
                        <div className="col-12 col-md-6">
                            <div className="p-3" style={{
                                background: '#fff',
                                borderRadius: 12,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                overflow: 'visible'
                            }}>
                                <h6 className="mb-3" style={{ fontSize: 14, fontWeight: 600 }}>
                                    {checkTranslation('{{lang}}Contracts Status Distribution')}
                                </h6>
                                <div style={{ height: 350, padding: '20px', overflow: 'hidden', position: 'relative', minHeight: 350 }}>
                                    <Doughnut
                                        data={rightChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            cutout: '25%',
                                            plugins: {
                                                legend: {
                                                    display: showLegend,
                                                    position: 'bottom',
                                                    labels: {
                                                        boxWidth: 14,
                                                        padding: 10,
                                                        usePointStyle: true,
                                                        pointStyle: 'circle',
                                                    },
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: function (context) {
                                                            const label = context.label || ''
                                                            const value = context.parsed || context.raw
                                                            const total = context.dataset.data.reduce((a, b) => a + b, 0)
                                                            const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                            return `${label}: ${value} (${percentage}%)`
                                                        },
                                                    },
                                                },
                                            },
                                            elements: {
                                                arc: {
                                                    borderWidth: 2,
                                                    borderColor: '#fff',
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Summary */}
                    <div className="col-12 mt-3">
                        <div className="p-3" style={{
                            background: `linear-gradient(135deg, ${colors.main1}10, #ffffff)`,
                            borderRadius: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: `1px solid ${colors.main1}30`
                        }}>
                            <h6 className="mb-3" style={{ fontSize: 14, fontWeight: 600, color: colors.main1 }}>
                                {translate('Summary')}
                            </h6>
                            <div className="mb-2">
                                <p className="mb-1" style={{ fontSize: 32, fontWeight: 'bold', color: colors.main1 }}>
                                    {totalCount}
                                </p>
                                <p className="mb-0" style={{ fontSize: 12, color: '#666' }}>
                                    {translate('Total Records')}
                                </p>
                            </div>
                            <div className="mt-3">
                                <p className="mb-1" style={{ fontSize: 12, color: '#666' }}>
                                    {translate('Contracts')}: {contractsCount}
                                </p>
                                <p className="mb-0" style={{ fontSize: 12, color: '#666' }}>
                                    {translate('Active Stages')}: {Object.values(currentStatusCounts).filter(count => count > 0).length}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default ContractsOverviewWidget

